import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import { AlertTriangle } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import TabsComponent from "@/components/tabs/tabs-component";
import type { TabItemProps } from "@/components/tabs/tab-component.type";
import {
  useFilterBuilder,
  useSortBuilder,
  type FilterableAttribute,
  type SortableAttribute,
  AttributeDataType,
  serializeFiltersToQuery,
  serializeSortsToQuery
} from "@/components/search-filter-bar";
import { useDebounce } from "@/hooks/use-debounce";
import { useScheduleCalendar } from "@/hooks/use-schedule-calendar";
import { useGetData } from "@/hooks/services/use-query-hooks";
import { GetAiRecommendationSchedules } from "@/hooks/services/pps/handlers/ai-recommendation-api";
import type { ViewMode } from "@/hooks/services/pps/handlers/schedule/job-schedule-types";
import { useWorkSpaceStore } from "@/stores/store";
import { WorkspaceToggle } from "@/components/workspace-toggle";

import JobGanttChart from "../job-scheduler/components/job-schedule";
import TechnicianGanttChart from "../job-scheduler/components/technician-schedule";
import MachineGanttChart from "../job-scheduler/components/machine-schedule";
import { ZOOM_MIN, ZOOM_MAX, ZOOM_STEP } from "../job-scheduler/utils";
import { buildAiRecommendationViews } from "./utils/ai-recommendation-mapper";

export const Route = createFileRoute("/plant-3/ai-recommendation/")({
  component: RouteComponent
});

const text = (id: string, label: string): FilterableAttribute => ({
  id,
  field: id,
  label,
  dataType: AttributeDataType.TEXT
});

const dateTime = (id: string, label: string): FilterableAttribute => ({
  id,
  field: id,
  label,
  dataType: AttributeDataType.DATETIME
});

// All fields are solver strings; the backend compares them on normalized keys (case/spacing-insensitive).
const JOB_FILTERS: FilterableAttribute[] = [
  text("jobOrderNo", "Job Order No"),
  text("serialNo", "Serial No"),
  text("partNumber", "Part Number"),
  text("customerName", "Customer Name"),
  text("processName", "Process"),
  text("technicianName", "Technician"),
  text("machineName", "Machine"),
  dateTime("startDate", "Start Date"),
  dateTime("eddDate", "End Date")
];

const TECHNICIAN_FILTERS: FilterableAttribute[] = [
  text("technicianName", "Technician"),
  text("shopName", "Shop"),
  text("jobOrderNo", "Job Order No"),
  text("processName", "Process"),
  text("machineName", "Machine"),
  dateTime("startTime", "Start Time"),
  dateTime("endTime", "End Time")
];

const MACHINE_FILTERS: FilterableAttribute[] = [
  text("machineName", "Machine"),
  text("shopName", "Shop"),
  text("jobOrderNo", "Job Order No"),
  text("processName", "Process"),
  text("technicianName", "Technician"),
  dateTime("startTime", "Start Time"),
  dateTime("endTime", "End Time")
];

const JOB_SORTS: SortableAttribute[] = [
  { id: "jobOrderNo", field: "jobOrderNo", label: "Job Order No" },
  { id: "serialNo", field: "serialNo", label: "Serial No" },
  { id: "partNumber", field: "partNumber", label: "Part Number" },
  { id: "processName", field: "processName", label: "Process Sequence" },
  { id: "technicianName", field: "technicianName", label: "Technician" },
  { id: "machineName", field: "machineName", label: "Machine" },
  { id: "startDate", field: "startDate", label: "Start Date" },
  { id: "eddDate", field: "eddDate", label: "End Date" },
  { id: "durationHours", field: "durationHours", label: "Duration" }
];

const RESOURCE_SORTS: SortableAttribute[] = [
  { id: "jobOrderNo", field: "jobOrderNo", label: "Job Order No" },
  { id: "processName", field: "processName", label: "Process Sequence" },
  { id: "startTime", field: "startTime", label: "Start Time" },
  { id: "endTime", field: "endTime", label: "End Time" },
  { id: "durationHours", field: "durationHours", label: "Duration" }
];

const countBadge = (count: number) => (
  <div className="aero:flex aero:items-center aero:justify-center aero:gap-2 aero:text-[10px] aero:h-[22px] aero:min-w-[22px] aero:px-1 aero:!rounded-full aero:bg-brand-primary-600">
    <div className="aero:text-white">{count}</div>
  </div>
);

/** Date range the calendar shows for `date` in the given view (weeks start on Monday, as in the scheduler). */
const rangeAround = (date: Date, viewMode: ViewMode) => {
  if (viewMode === "daily") {
    return { from: date, to: date };
  }
  if (viewMode === "monthly") {
    return {
      from: dayjs(date).startOf("month").toDate(),
      to: dayjs(date).endOf("month").toDate()
    };
  }
  const day = dayjs(date);
  const monday = day.subtract((day.day() + 6) % 7, "day").startOf("day");
  return { from: monday.toDate(), to: monday.add(6, "day").toDate() };
};

function RouteComponent() {
  const [activeTab, setActiveTab] = useState("job");
  const { workspace } = useWorkSpaceStore();

  const [viewMode, setViewMode] = useState<ViewMode>("weekly");
  const [zoomLevel, setZoomLevel] = useState<number>(20);
  const onZoomIn = () => setZoomLevel((z) => Math.min(z + ZOOM_STEP, ZOOM_MAX));
  const onZoomOut = () => setZoomLevel((z) => Math.max(z - ZOOM_STEP, ZOOM_MIN));
  const { dateRange, setDateRange } = useScheduleCalendar("weekly");

  useEffect(() => {
    setZoomLevel(activeTab === "job" ? 20 : 30);
  }, [activeTab]);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const { filters, addFilter, updateFilter, removeFilter, clearFilters } =
    useFilterBuilder();
  const { sorts, addSort, updateSort, removeSort, clearSorts } =
    useSortBuilder();
  const debouncedFilters = useDebounce(filters, 500);
  const debouncedSorts = useDebounce(sorts, 500);

  // Expanded to whole months (like the Processes page) so Day/Week/Month switches hit the cache.
  const queryParams = useMemo(
    () => ({
      workspaceId: workspace?.id,
      search: debouncedSearch || undefined,
      filters:
        debouncedFilters.length > 0
          ? serializeFiltersToQuery(debouncedFilters)
          : undefined,
      sorts:
        debouncedSorts.length > 0
          ? serializeSortsToQuery(debouncedSorts)
          : undefined,
      from: dateRange.from
        ? dayjs(dateRange.from).startOf("month").format("YYYY-MM-DD")
        : undefined,
      to:
        (dateRange.to ?? dateRange.from)
          ? dayjs(dateRange.to ?? dateRange.from)
              .endOf("month")
              .format("YYYY-MM-DD")
          : undefined
    }),
    [
      workspace?.id,
      debouncedSearch,
      debouncedFilters,
      debouncedSorts,
      dateRange.from,
      dateRange.to
    ]
  );

  const { data, isLoading, error } = useGetData(
    GetAiRecommendationSchedules(queryParams)
  );

  const views = useMemo(
    () => buildAiRecommendationViews(data?.schedules ?? []),
    [data?.schedules]
  );

  const runStart = data?.runStartDate ? dayjs(data.runStartDate) : null;
  const runEnd = data?.runEndDate ? dayjs(data.runEndDate) : null;

  // Solver output is often for a different week than today; open on the plan once, then leave navigation to the user.
  const hasAutoJumped = useRef(false);
  useEffect(() => {
    if (hasAutoJumped.current || !runStart || !runEnd) return;
    hasAutoJumped.current = true;

    const visibleFrom = dayjs(dateRange.from ?? undefined).startOf("day");
    const visibleTo = dayjs(dateRange.to ?? dateRange.from ?? undefined).endOf("day");
    const overlaps = !runEnd.isBefore(visibleFrom) && !runStart.isAfter(visibleTo);
    if (!overlaps) {
      setDateRange(rangeAround(runStart.toDate(), viewMode));
    }
  }, [runStart, runEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  const sharedProps = {
    searchValue: search,
    onSearchChange: setSearch,
    dynamicFilters: filters,
    onAddFilter: addFilter,
    onUpdateFilter: updateFilter,
    onRemoveFilter: removeFilter,
    onClearFilters: clearFilters,
    dynamicSorts: sorts,
    onAddSort: addSort,
    onUpdateSort: updateSort,
    onRemoveSort: removeSort,
    onClearSorts: clearSorts,
    dateRange,
    onDateRangeChange: setDateRange,
    viewMode,
    onViewModeChange: setViewMode,
    zoomLevel,
    onZoomIn,
    onZoomOut,
    isLoading
  };

  const tabs: TabItemProps[] = [
    {
      value: "job",
      label: "Job",
      icon: countBadge(views.jobs.length),
      content: (
        <JobGanttChart
          {...sharedProps}
          jobs={views.jobs}
          filterableAttributes={JOB_FILTERS}
          sortableAttributes={JOB_SORTS}
        />
      )
    },
    {
      value: "technician",
      label: "Technician",
      icon: countBadge(views.technicians.length),
      content: (
        <TechnicianGanttChart
          {...sharedProps}
          technicians={views.technicians}
          filterableAttributes={TECHNICIAN_FILTERS}
          sortableAttributes={[
            { id: "technicianName", field: "technicianName", label: "Technician" },
            ...RESOURCE_SORTS
          ]}
        />
      )
    },
    {
      value: "machine",
      label: "Machine",
      icon: countBadge(views.machines.length),
      content: (
        <MachineGanttChart
          {...sharedProps}
          machines={views.machines}
          filterableAttributes={MACHINE_FILTERS}
          sortableAttributes={[
            { id: "machineName", field: "machineName", label: "Machine" },
            ...RESOURCE_SORTS
          ]}
        />
      )
    }
  ];

  return (
    <div className="aero:flex aero:flex-col aero:gap-3 aero:w-full aero:max-w-full aero:overflow-hidden">
      <div className="aero:flex aero:items-center aero:justify-between aero:flex-wrap aero:gap-4 aero:mb-1">
        <div>
          <h1 className="aero:flex aero:items-center aero:gap-2 aero:text-2xl aero:font-bold">
            AI Recommendation
          </h1>
          <p className="aero:text-muted-foreground aero:text-sm">
            Latest process allocation proposed by the OR-AI production planning solver
          </p>
        </div>
        <WorkspaceToggle />
      </div>

      {data && !data.runId && !error && (
        <Alert>
          <AlertTriangle className="aero:h-4 aero:w-4" />
          <AlertTitle>No AI recommendation yet</AlertTitle>
          <AlertDescription>
            No solver run has been synced from the OR-AI planner yet. It will appear after the next scheduled sync.
          </AlertDescription>
        </Alert>
      )}

      {data?.runId && (
        <div className="aero:flex aero:flex-wrap aero:items-center aero:gap-2 aero:text-xs aero:text-muted-foreground">
          {data.source === "mock" && <Badge variant="warning">Mock data</Badge>}
          {data.solverStatus && (
            <Badge variant={data.solverStatus === "OPTIMAL" ? "success" : "secondary"}>
              {data.solverStatus}
            </Badge>
          )}
          {data.runId && <span>Run {data.runId}</span>}
          {data.generatedAt && (
            <span>· Generated {dayjs(data.generatedAt).format("DD MMM YYYY HH:mm")}</span>
          )}
          {data.syncedAt && (
            <span>· Synced {dayjs(data.syncedAt).format("DD MMM YYYY HH:mm")}</span>
          )}
          <span>
            · {data.sourceTotalRows} steps across {data.sourceUniqueJobs} job orders
          </span>
          {runStart && runEnd && (
            <span>
              · Plan window {runStart.format("DD MMM HH:mm")} – {runEnd.format("DD MMM YYYY HH:mm")}
            </span>
          )}
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="aero:h-4 aero:w-4" />
          <AlertTitle>AI recommendation is unavailable</AlertTitle>
          <AlertDescription>
            The stored solver run could not be loaded. Please try again later.
          </AlertDescription>
        </Alert>
      )}

      <TabsComponent
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        scrollable={false}
        swapIconPosition
      />
    </div>
  );
}
